import i18n from '../i18n';

const activeLocale = (): string => {
  switch (i18n.language?.split('-')[0]) {
    case 'en':
      return 'en-GB';
    case 'kk':
      return 'kk-KZ';
    default:
      return 'ru-RU';
  }
};

export const formatNumber = (value: number | null | undefined, fractionDigits = 2): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(activeLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

/**
 * Nominal capacity in m³ read off the standard tank designation - РВС-2000, RVS-1000,
 * РГС-75 and so on, where the number is the rated volume. Returns null when the name
 * does not carry one, in which case the caller must not render a fill gauge: this is a
 * convention, not a stored field, and a wrong capacity would be worse than none.
 */
export const nominalCapacity = (name?: string | null): number | null => {
  if (!name) return null;

  // Cyrillic РВС/РГС or Latin RVS/RGS, followed by the rated volume
  const match = name.match(/(?:[РP][ВB][СC]|[РP][ГG][СC]|RVS|RGS)\s*[-–—]?\s*(\d{2,6})/i);
  if (!match) return null;

  const capacity = Number(match[1]);
  return Number.isFinite(capacity) && capacity > 0 ? capacity : null;
};

/**
 * Age of a reading in minutes.
 *
 * The backend sends an already-formatted wall-clock string in the server's zone
 * ("yyyy-MM-dd HH:mm:ss" via DateUtils.instantToDbStringServerZone) with no offset.
 * Safari refuses to parse that form, so it is parsed explicitly. The parts are read as
 * local time, which matches the operators watching this screen from the same zone as
 * the server; if the two ever diverge the age would be off by that difference.
 */
export const readingAgeMinutes = (dateTime?: string | null): number | null => {
  if (!dateTime?.trim()) return null;

  let ms: number;
  const iso = dateTime.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  const dotted = dateTime.match(/^(\d{2})\.(\d{2})\.(\d{4})[ ,T]+(\d{2}):(\d{2})(?::(\d{2}))?/);

  if (iso) {
    const [, yyyy, MM, dd, hh, mi, ss] = iso;
    ms = new Date(+yyyy, +MM - 1, +dd, +hh, +mi, +(ss ?? 0)).getTime();
  } else if (dotted) {
    const [, dd, MM, yyyy, hh, mi, ss] = dotted;
    ms = new Date(+yyyy, +MM - 1, +dd, +hh, +mi, +(ss ?? 0)).getTime();
  } else {
    ms = new Date(dateTime).getTime();
  }

  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.round((Date.now() - ms) / 60000));
};
